/**
 * Webhook endpoints tab - shown on the Partner detail page under "Integrations".
 *
 * Lets partner_admin self-service register, view, and disable webhook endpoints
 * that receive outbound payment-status events (payment.approved, etc.).
 * Signing secret is displayed once at creation and never retrievable again.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCopy, Check, Plus, Radio, Trash2, AlertTriangle } from "lucide-react";
import { useRef, useState } from "react";

import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type {
  WebhookEndpoint,
  WebhookEndpointCreatedResponse,
  WebhookEventKind,
  Uuid,
} from "@tilt/admin-sdk";

// ---------------------------------------------------------------------- //
// Constants                                                               //
// ---------------------------------------------------------------------- //

const ALL_EVENT_KINDS: { kind: WebhookEventKind; label: string; description: string }[] = [
  {
    kind: "payment.approved",
    label: "payment.approved",
    description: "Payment moved from pending to approved/settled",
  },
  {
    kind: "payment.cancelled",
    label: "payment.cancelled",
    description: "Payment cancelled or expired",
  },
  {
    kind: "payment.refunded",
    label: "payment.refunded",
    description: "Cashier-initiated refund completed",
  },
  {
    kind: "payment.voided",
    label: "payment.voided",
    description: "Order voided; associated charges retracted",
  },
  {
    kind: "payment.failed",
    label: "payment.failed",
    description: "Payment polling exhausted; terminal failure",
  },
  {
    kind: "payment.settled",
    label: "payment.settled",
    description: "ACH payment cleared (post-approval settlement)",
  },
];

// ---------------------------------------------------------------------- //
// Main tab                                                                //
// ---------------------------------------------------------------------- //

export function WebhookEndpointsTab(props: { partnerId: Uuid; canWrite: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [createdEndpoint, setCreatedEndpoint] = useState<WebhookEndpointCreatedResponse | null>(
    null,
  );
  const [disableTarget, setDisableTarget] = useState<WebhookEndpoint | null>(null);

  const endpoints = useQuery({
    queryKey: ["webhook-endpoints", props.partnerId],
    queryFn: () => apiClient.listWebhookEndpoints(props.partnerId),
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["webhook-endpoints", props.partnerId] });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Webhook Endpoints</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Receive outbound payment-status events via HTTPS POST with HMAC-SHA256 signatures.
          </p>
        </div>
        {props.canWrite && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> New endpoint
          </button>
        )}
      </div>

      {/* Signature reference */}
      <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
        <p className="font-medium text-foreground">Verifying signatures</p>
        <p className="text-muted-foreground">
          Each delivery includes{" "}
          <code className="font-mono">X-Tilt-Signature: sha256=&lt;hex&gt;</code>. Compute{" "}
          <code className="font-mono">HMAC-SHA256(signing_secret_bytes, raw_request_body)</code> and
          compare to verify authenticity.
        </p>
      </div>

      {endpoints.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          {describe(endpoints.error)}
        </div>
      )}

      {endpoints.isLoading && <p className="text-xs text-muted-foreground">Loading endpoints…</p>}

      {endpoints.data && endpoints.data.endpoints.length === 0 && (
        <div className="rounded-md border border-dashed p-6 text-center">
          <Radio className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">No webhook endpoints yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Register an HTTPS URL to start receiving payment events.
          </p>
        </div>
      )}

      {endpoints.data && endpoints.data.endpoints.length > 0 && (
        <div className="space-y-2">
          {endpoints.data.endpoints.map((ep: WebhookEndpoint) => (
            <EndpointRow
              key={ep.id}
              endpoint={ep}
              canWrite={props.canWrite}
              onDisable={() => setDisableTarget(ep)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreateDialog
          partnerId={props.partnerId}
          onClose={() => setCreateOpen(false)}
          onCreated={(ep) => {
            invalidate();
            setCreateOpen(false);
            setCreatedEndpoint(ep);
          }}
        />
      )}

      {createdEndpoint !== null && (
        <SecretRevealModal endpoint={createdEndpoint} onClose={() => setCreatedEndpoint(null)} />
      )}

      {disableTarget !== null && (
        <DisableDialog
          partnerId={props.partnerId}
          endpoint={disableTarget}
          onClose={() => setDisableTarget(null)}
          onDisabled={() => {
            invalidate();
            setDisableTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- //
// EndpointRow                                                             //
// ---------------------------------------------------------------------- //

function EndpointRow(props: {
  endpoint: WebhookEndpoint;
  canWrite: boolean;
  onDisable: () => void;
}): JSX.Element {
  const { endpoint } = props;
  const kinds =
    endpoint.event_kinds.length === 0 ? "All event kinds" : endpoint.event_kinds.join(", ");

  return (
    <div className="flex items-start justify-between rounded-md border p-3">
      <div className="min-w-0 space-y-0.5">
        <p className="truncate text-xs font-mono font-medium text-foreground">{endpoint.url}</p>
        {endpoint.description && (
          <p className="text-xs text-muted-foreground">{endpoint.description}</p>
        )}
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Events: </span>
          {kinds}
        </p>
        <p className="text-xs text-muted-foreground">
          Added {endpoint.created_at ? new Date(endpoint.created_at).toLocaleDateString() : "-"}
        </p>
      </div>
      {props.canWrite && (
        <button
          type="button"
          onClick={props.onDisable}
          title="Disable endpoint"
          className="ml-3 flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- //
// CreateDialog                                                            //
// ---------------------------------------------------------------------- //

function CreateDialog(props: {
  partnerId: Uuid;
  onClose: () => void;
  onCreated: (ep: WebhookEndpointCreatedResponse) => void;
}): JSX.Element {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [selectedKinds, setSelectedKinds] = useState<Set<WebhookEventKind>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const req: Parameters<typeof apiClient.createWebhookEndpoint>[1] = {
        url,
        event_kinds: selectedKinds.size > 0 ? [...selectedKinds] : [],
      };
      const trimmedDesc = description.trim();
      if (trimmedDesc) req.description = trimmedDesc;
      return apiClient.createWebhookEndpoint(props.partnerId, req);
    },
    onSuccess: (data) => props.onCreated(data),
    onError: (err) => setError(describe(err)),
  });

  const toggleKind = (kind: WebhookEventKind) => {
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  return (
    <ModalOverlay ariaLabel="Register webhook endpoint" onClose={props.onClose}>
      <div className="w-full max-w-md space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
        <h3 className="text-sm font-semibold">Register webhook endpoint</h3>

        <label className="block space-y-1">
          <span className="text-xs font-medium">URL *</span>
          <input
            id="wh-url"
            type="url"
            placeholder="https://partner.example/webhooks/tilt"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium">Description (optional)</span>
          <input
            id="wh-description"
            type="text"
            placeholder="Production webhook receiver"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </label>

        <div className="space-y-1">
          <p className="text-xs font-medium">
            Event kinds{" "}
            <span className="font-normal text-muted-foreground">
              (leave all unchecked to receive all)
            </span>
          </p>
          <div className="space-y-1">
            {ALL_EVENT_KINDS.map(({ kind, label, description: desc }) => (
              <label
                key={kind}
                aria-label={label}
                className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-primary"
                  checked={selectedKinds.has(kind)}
                  onChange={() => toggleKind(kind)}
                />
                <span>
                  <span className="text-xs font-mono">{label}</span>
                  <span className="ml-1.5 text-xs text-muted-foreground">{desc}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!url || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------- //
// SecretRevealModal - one-shot signing secret display                     //
// ---------------------------------------------------------------------- //

function SecretRevealModal(props: {
  endpoint: WebhookEndpointCreatedResponse;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const secretRef = useRef<HTMLInputElement>(null);

  const copy = async () => {
    await navigator.clipboard.writeText(props.endpoint.signing_secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Only allow closing after the user confirms they've saved the secret.
  const handleClose = confirmed ? props.onClose : () => {};

  return (
    <ModalOverlay ariaLabel="Save signing secret" onClose={handleClose}>
      <div className="w-full max-w-md space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <div>
            <h3 className="text-sm font-semibold">Save your signing secret</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              This is the only time this value will be shown. Store it securely - it cannot be
              retrieved again.
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-xs font-medium">URL</p>
          <p className="truncate rounded-md bg-muted/40 px-3 py-2 text-xs font-mono">
            {props.endpoint.url}
          </p>
        </div>

        <div className="space-y-1">
          <label htmlFor="wh-signing-secret" className="text-xs font-medium">
            Signing secret (hex)
          </label>
          <div className="flex items-center gap-2">
            <input
              ref={secretRef}
              id="wh-signing-secret"
              type="text"
              readOnly
              value={props.endpoint.signing_secret}
              className="flex-1 rounded-md border bg-background px-3 py-1.5 text-xs font-mono select-all focus:outline-none"
            />
            <button
              type="button"
              onClick={copy}
              className={cn(
                "flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs",
                copied ? "border-green-500 text-green-600" : "hover:bg-muted/50",
              )}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <ClipboardCopy className="h-3.5 w-3.5" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-xs">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-primary"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          I have saved the signing secret and understand it won&apos;t be shown again.
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={!confirmed}
            onClick={props.onClose}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Done
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------- //
// DisableDialog                                                           //
// ---------------------------------------------------------------------- //

function DisableDialog(props: {
  partnerId: Uuid;
  endpoint: WebhookEndpoint;
  onClose: () => void;
  onDisabled: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => apiClient.disableWebhookEndpoint(props.partnerId, props.endpoint.id),
    onSuccess: props.onDisabled,
    onError: (err) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel="Disable webhook endpoint" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
        <h3 className="text-sm font-semibold">Disable webhook endpoint?</h3>
        <p className="text-xs text-muted-foreground">
          No further events will be delivered to{" "}
          <code className="font-mono">{props.endpoint.url}</code>. In-flight deliveries will
          complete; pending retries will be abandoned.
        </p>

        {error && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? "Disabling…" : "Disable"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
